<script setup lang="ts">
import UiIcon from '../../icons/Icon.vue'
import type { UiIconName } from '../../icons/definitions'
import UiIconButton from './IconButton.vue'
import { useToast, type ToastVariant } from '../composables/useToast'

/**
 * `<UiToastHost>` — mount once, at the app root.
 *
 * The queue lives in `useToast()`; this is only the view. That split is what
 * lets an API error handler in a Pinia store raise a toast without a component
 * instance to inject from.
 *
 * ## The live region, which is the whole accessibility story
 *
 * The container is the live region and it is **always in the DOM**, empty or
 * not. A live region created at the moment content arrives is not announced —
 * the screen reader has to be observing the node before it changes. This is
 * the single most common reason toasts are silent for screen-reader users.
 *
 * `role="status"` (polite) for info and success; `role="alert"` (assertive)
 * for warnings and failures, which interrupt. A failed payout deserves the
 * interruption; "Saved" does not.
 *
 * Hovering or focusing a toast pauses its dismissal timer, so a message cannot
 * expire while it is being read or while the user is reaching for its action.
 */

const { toasts, dismiss, pause, resume } = useToast()

const TONE_ICON: Record<ToastVariant, UiIconName> = {
  info: 'info',
  success: 'check-circle',
  warning: 'alert-triangle',
  danger: 'alert-circle',
}

withDefaults(
  defineProps<{
    /** Where the stack sits. `bottom-end` keeps it clear of the top nav. */
    position?: 'top-end' | 'top-center' | 'bottom-end' | 'bottom-center'
  }>(),
  { position: 'bottom-end' },
)
</script>

<template>
  <Teleport to="body">
    <div class="ui-toast-host" :class="`ui-toast-host--${position}`">
      <!-- Two regions, because a single region cannot be both polite and
           assertive, and both must exist before any toast arrives. -->
      <div class="ui-toast-host__region" role="status" aria-live="polite" aria-relevant="additions text">
        <TransitionGroup name="ui-toast">
          <div
            v-for="toast in toasts.filter((candidate) => candidate.variant === 'info' || candidate.variant === 'success')"
            :key="toast.id"
            class="ui-toast"
            :class="`ui-toast--${toast.variant}`"
            @pointerenter="pause(toast.id)"
            @pointerleave="resume(toast.id)"
            @focusin="pause(toast.id)"
            @focusout="resume(toast.id)"
          >
            <UiIcon :name="TONE_ICON[toast.variant]" class="ui-toast__icon" size="1.15em" />
            <div class="ui-toast__body">
              <p class="ui-toast__title">{{ toast.title }}</p>
              <p v-if="toast.description" class="ui-toast__description">{{ toast.description }}</p>
              <button
                v-if="toast.action"
                type="button"
                class="ui-toast__action ui-focusable"
                @click="toast.action.onClick(); dismiss(toast.id)"
              >
                {{ toast.action.label }}
              </button>
            </div>
            <UiIconButton class="ui-toast__close" name="x" label="Dismiss" size="sm" @click="dismiss(toast.id)" />
          </div>
        </TransitionGroup>
      </div>

      <div class="ui-toast-host__region" role="alert" aria-live="assertive" aria-relevant="additions text">
        <TransitionGroup name="ui-toast">
          <div
            v-for="toast in toasts.filter((candidate) => candidate.variant === 'warning' || candidate.variant === 'danger')"
            :key="toast.id"
            class="ui-toast"
            :class="`ui-toast--${toast.variant}`"
            @pointerenter="pause(toast.id)"
            @pointerleave="resume(toast.id)"
            @focusin="pause(toast.id)"
            @focusout="resume(toast.id)"
          >
            <UiIcon :name="TONE_ICON[toast.variant]" class="ui-toast__icon" size="1.15em" />
            <div class="ui-toast__body">
              <p class="ui-toast__title">{{ toast.title }}</p>
              <p v-if="toast.description" class="ui-toast__description">{{ toast.description }}</p>
              <!-- The request id is what support asks for first; putting it on
                   the failure toast saves a round trip every single time. -->
              <p v-if="toast.requestId" class="ui-toast__request-id">Ref {{ toast.requestId }}</p>
              <button
                v-if="toast.action"
                type="button"
                class="ui-toast__action ui-focusable"
                @click="toast.action.onClick(); dismiss(toast.id)"
              >
                {{ toast.action.label }}
              </button>
            </div>
            <UiIconButton class="ui-toast__close" name="x" label="Dismiss" size="sm" @click="dismiss(toast.id)" />
          </div>
        </TransitionGroup>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.ui-toast-host {
  position: fixed;
  z-index: var(--z-toast);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4);
  pointer-events: none;
  max-inline-size: min(420px, 100vw);
}

.ui-toast-host--top-end {
  inset-block-start: 0;
  inset-inline-end: 0;
}

.ui-toast-host--bottom-end {
  inset-block-end: 0;
  inset-inline-end: 0;
}

.ui-toast-host--top-center {
  inset-block-start: 0;
  inset-inline: 50%;
  transform: translateX(-50%);
}

.ui-toast-host--bottom-center {
  inset-block-end: 0;
  inset-inline: 50%;
  transform: translateX(-50%);
}

.ui-toast-host__region {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.ui-toast {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-3) var(--space-3) var(--space-4);
  padding-inline: var(--space-4) var(--space-3);
  background: var(--surface-raised);
  border: 1px solid var(--border);
  border-inline-start: 3px solid var(--border-strong);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  pointer-events: auto;
  min-inline-size: 280px;
}

.ui-toast--info {
  border-inline-start-color: var(--info);
}

.ui-toast--success {
  border-inline-start-color: var(--positive);
}

.ui-toast--warning {
  border-inline-start-color: var(--warning);
}

.ui-toast--danger {
  border-inline-start-color: var(--danger);
}

.ui-toast__icon {
  flex: none;
  margin-block-start: 1px;
}

.ui-toast--info .ui-toast__icon {
  color: var(--info);
}

.ui-toast--success .ui-toast__icon {
  color: var(--positive);
}

.ui-toast--warning .ui-toast__icon {
  color: var(--warning);
}

.ui-toast--danger .ui-toast__icon {
  color: var(--danger);
}

.ui-toast__body {
  flex: 1;
  min-inline-size: 0;
}

.ui-toast__title {
  font-size: var(--text-base);
  font-weight: var(--weight-medium);
  color: var(--text);
  line-height: var(--leading-tight);
}

.ui-toast__description {
  margin-block-start: var(--space-1);
  font-size: var(--text-sm);
  color: var(--text-muted);
  line-height: var(--leading-normal);
}

.ui-toast__request-id {
  margin-block-start: var(--space-1);
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  color: var(--text-subtle);
  overflow-wrap: anywhere;
}

.ui-toast__action {
  margin-block-start: var(--space-2);
  border: 0;
  background: transparent;
  padding: 0;
  color: var(--brand);
  font: inherit;
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}

.ui-toast__close {
  flex: none;
}

/* ---- Transition ---------------------------------------------------------- */

.ui-toast-enter-active,
.ui-toast-leave-active {
  transition:
    opacity var(--duration-base) var(--ease-out),
    transform var(--duration-base) var(--ease-out);
}

.ui-toast-enter-from,
.ui-toast-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.ui-toast-leave-active {
  position: absolute;
}

@media (prefers-reduced-motion: reduce) {
  .ui-toast-enter-active,
  .ui-toast-leave-active {
    transition-duration: 1ms;
  }

  .ui-toast-enter-from,
  .ui-toast-leave-to {
    transform: none;
  }
}

@media (max-width: 640px) {
  .ui-toast-host {
    max-inline-size: 100vw;
    inset-inline: 0;
    transform: none;
  }

  .ui-toast {
    min-inline-size: 0;
  }
}
</style>
